'use client'

import { type ReactNode, useEffect, useRef, useState } from 'react'

import { createPortal } from 'react-dom'

import { ArticleUiIcon } from '@/components/icons/article-ui-icon'
import { useTranslation } from '@/hooks/client/use-translation'

interface ListicleShowMoreProps {
  initial: ReactNode
  extra?: ReactNode
  hasMore: boolean
}

const ArrowSvg = ({ showAll }: { showAll: boolean }) => (
  <ArticleUiIcon
    id="arrow-right"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    className={`text-white ${showAll ? '-rotate-90' : 'rotate-90'}`}
  />
)

export const ListicleShowMore = ({ initial, extra, hasMore }: ListicleShowMoreProps) => {
  const { t } = useTranslation()
  const [showAll, setShowAll] = useState(false)

  return (
    <div>
      {initial}
      {showAll && extra}
      {hasMore && (
        <button
          type="button"
          onClick={() => setShowAll((expanded) => !expanded)}
          aria-expanded={showAll}
          data-ht-listicle-toggle=""
          data-tracking="ignore"
          className="flex w-full cursor-pointer items-center justify-center gap-[16px] pt-6"
        >
          <span className="text-navy text-xl font-light">
            {showAll ? t('client.article.showFewer', 'Show fewer') : t('client.article.showMore', 'Show more')}
          </span>
          <div className="bg-navy rounded-full p-[15px]">
            <ArrowSvg showAll={showAll} />
          </div>
        </button>
      )}
    </div>
  )
}

const InfoIcon = () => (
  <ArticleUiIcon id="info" width="14" height="14" viewBox="0 0 24 24" className="lucide lucide-info" />
)

export const ListicleScoreBadge = ({ score }: { score: string }) => {
  const { t } = useTranslation()
  const [showPopover, setShowPopover] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({})
  const [side, setSide] = useState<'above' | 'below'>('above')

  useEffect(() => {
    if (showPopover && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      const spaceAbove = rect.top
      const showAbove = spaceAbove > 300
      setSide(showAbove ? 'above' : 'below')
      setPopoverStyle({
        position: 'fixed',
        top: showAbove ? rect.top - 8 : rect.bottom + 8,
        left: rect.left + rect.width / 2,
        transform: showAbove ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
        zIndex: 9999,
      })
    }
  }, [showPopover])

  return (
    <div className="relative inline-block">
      <div className="flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs shadow-sm transition-all duration-200 select-none">
        <div className="text-lapis flex items-baseline gap-1 font-bold">
          <span>{score}</span>
          <span className="text-[0.8em] font-normal text-slate-400">/5</span>
        </div>
        <span className="flex items-center">
          <button
            ref={buttonRef}
            type="button"
            data-tracking="ignore"
            className="ml-1 cursor-pointer rounded-full p-0.5 text-slate-400 transition-colors duration-200 hover:text-lapis focus:outline-none focus-visible:ring-2"
            aria-label={t('client.article.scoreDetails', 'Score Details')}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setShowPopover(!showPopover)
            }}
          >
            <InfoIcon />
          </button>
        </span>
      </div>
      {showPopover &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[9998]" onClick={() => setShowPopover(false)} />
            <div style={popoverStyle} className="bg-navy w-72 rounded-lg p-5 text-white shadow-xl ring-1 ring-white/10">
              <div className="mb-3 flex items-center justify-between border-b border-white/20 pb-3">
                <h3 className="text-base font-bold tracking-wide text-white">
                  {t('client.article.scoreDetails', 'Score Details')}
                </h3>
                <span className="text-lg font-bold text-blue-400">{score}/5</span>
              </div>
              <p className="text-sm leading-relaxed text-slate-200">
                {t(
                  'product.scoreTooltip',
                  'The HearingTracker score is the weighted average of all components scores seen on this score card.'
                )}
              </p>
              <div
                className={`bg-navy absolute left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 ring-1 ring-white/10 ${side === 'above' ? '-bottom-1.5' : '-top-1.5'}`}
              />
            </div>
          </>,
          document.body
        )}
    </div>
  )
}
